/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function ComplexStatisticsCard({ color, title, count, percentage, icon }) {
  const theme = useTheme();
  
  // Define breakpoints for height-based responsive design
  const isExtraSmallHeight = useMediaQuery('(max-height: 600px)');
  const isSmallHeight = useMediaQuery('(max-height: 768px)');
  const isMediumHeight = useMediaQuery('(max-height: 1024px)');
  
  // Responsive sizing configuration
  const responsiveConfig = {
    iconBox: {
      width: isExtraSmallHeight ? "3rem" : isSmallHeight ? "3.5rem" : "4rem",
      height: isExtraSmallHeight ? "3rem" : isSmallHeight ? "3.5rem" : "4rem",
      mt: isExtraSmallHeight ? -2 : isSmallHeight ? -2.5 : -3,
    },
    icon: {
      fontSize: isExtraSmallHeight ? "small" : isSmallHeight ? "medium" : "medium",
    },
    title: {
      variant: isExtraSmallHeight ? "caption" : isSmallHeight ? "button" : "button",
      fontSize: isExtraSmallHeight ? "0.7rem" : isSmallHeight ? "0.8rem" : "0.875rem",
    },
    count: {
      variant: isExtraSmallHeight ? "h6" : isSmallHeight ? "h5" : "h4",
      fontSize: isExtraSmallHeight ? "1rem" : isSmallHeight ? "1.25rem" : "1.5rem",
    },
    percentage: {
      variant: isExtraSmallHeight ? "caption" : "button",
      fontSize: isExtraSmallHeight ? "0.65rem" : isSmallHeight ? "0.75rem" : "0.875rem",
    },
    padding: {
      card: isExtraSmallHeight ? 1 : isSmallHeight ? 1.5 : 2,
      bottom: isExtraSmallHeight ? 1 : isSmallHeight ? 1.5 : 2,
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={responsiveConfig.padding.card}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color={color === "light" ? "dark" : "white"}
          coloredShadow={color}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={responsiveConfig.iconBox.width}
          height={responsiveConfig.iconBox.height}
          mt={responsiveConfig.iconBox.mt}
        >
          <Icon fontSize={responsiveConfig.icon.fontSize} color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography 
            variant={responsiveConfig.title.variant}
            fontWeight="light" 
            color="text"
            sx={{ 
              fontSize: responsiveConfig.title.fontSize,
              lineHeight: isExtraSmallHeight ? 1.2 : 1.25,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: isExtraSmallHeight ? 2 : 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </MDTypography>
          <MDTypography 
            variant={responsiveConfig.count.variant}
            sx={{ 
              fontSize: responsiveConfig.count.fontSize,
              lineHeight: isExtraSmallHeight ? 1.1 : 1.2,
              mt: isExtraSmallHeight ? 0.25 : 0.5,
            }}
          >
            {count}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider />
      <MDBox pb={responsiveConfig.padding.bottom} px={responsiveConfig.padding.card}>
        <MDTypography 
          component="p" 
          variant={responsiveConfig.percentage.variant}
          color="text" 
          display="flex"
          sx={{ 
            fontSize: responsiveConfig.percentage.fontSize,
            lineHeight: isExtraSmallHeight ? 1.2 : 1.4,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <MDTypography
            component="span"
            variant={responsiveConfig.percentage.variant}
            fontWeight="bold"
            color={percentage.color}
            sx={{ 
              fontSize: responsiveConfig.percentage.fontSize,
              mr: 0.5,
            }}
          >
            {percentage.amount}
          </MDTypography>
          <span style={{ 
            fontSize: responsiveConfig.percentage.fontSize,
            wordBreak: 'break-word',
            lineHeight: isExtraSmallHeight ? 1.2 : 1.4,
          }}>
            {percentage.label}
          </span>
        </MDTypography>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;
